import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCopy,
  DoorOpen,
  FileSpreadsheet,
  FileText,
  Flag,
  Loader2,
  Paperclip,
  Pencil,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import { SMBC_GFD_PLAYBOOK } from './playbook';
import { SEED_INITIATIVE } from './seedInitiative';
import {
  applyConfirmationsToPlaybook,
  buildPacket,
  exportBlocked,
  initiativeFromGenerate,
  isRequiredVisible,
  loadConfirmations,
  loadEdits,
  packetGaps,
  packetStats,
  saveConfirmation,
  saveEdits,
  isEmptyValue,
  valueToString,
} from './mapper';
import { buildChampionEmail } from './exportScript';
import { downloadChampionExcel } from './exportExcel';
import { generateGfdPacket, loadGfdSession, patchGfdFills, patchReadiness, runGfdInterview, runGfdSimulation, applyGfdSimulation, runGfdResearch, runGfdAccuracy, decideGfdFinding, downloadWalkthroughDocx, downloadBusinessCaseDocx, downloadBusinessCaseXlsx } from './gfdApi';
import {
  AccuracyFinding,
  ConfirmedOption,
  FillState,
  FieldValue,
  GfdDriver,
  GfdManifest,
  IntakeNeed,
  PacketField,
  PlaybookConfidence,
  ReadinessItem,
  ResearchPacket,
  SeedInitiative,
} from './types';
import { DEFAULT_READINESS } from './readiness';

type TabId = 'intake' | 'summary' | 'packet' | 'queue' | 'studio' | 'accuracy' | 'readiness' | 'export';
type PacketFilter = 'all' | 'confirm' | 'low' | 'empty' | 'queue';

const TABS: { id: TabId; label: string }[] = [
  { id: 'intake', label: '1. Documents' },
  { id: 'summary', label: '2. Summary' },
  { id: 'packet', label: '3. Field review' },
  { id: 'queue', label: '4. Clarification' },
  { id: 'studio', label: '5. Business case' },
  { id: 'accuracy', label: '6. Accuracy' },
  { id: 'readiness', label: '7. Readiness' },
  { id: 'export', label: '8. Export' },
];

const ACCEPT_EXT = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.md,.json,.html,.htm,.rtf,.eml,.msg';
const ALLOWED_FILE = /\.(pdf|docx?|pptx?|xlsx?|csv|txt|md|json|html?|rtf|eml|msg)$/i;

const NUMERIC_SEED_FIELDS = new Set(['fy_cost', 'cost_avoidance', 'cost_of_inaction', 'additional_headcount']);

function seedDriversFromInitiative(initiative: SeedInitiative): GfdDriver[] {
  return (initiative.fills || [])
    .filter((f) => NUMERIC_SEED_FIELDS.has(f.fieldId) && typeof f.value === 'number' && Number(f.value) > 0)
    .map((f) => {
      const n = Number(f.value);
      const fte = f.fieldId === 'additional_headcount';
      return {
        id: `extracted-${f.fieldId}`,
        gfdFieldId: f.fieldId,
        name: `Extracted ${f.fieldId.replace(/_/g, ' ')}`,
        unit: fte ? 'FTE' : 'USD',
        optimistic: Math.round(n * 0.85 * (fte ? 10 : 1)) / (fte ? 10 : 1),
        most_likely: n,
        pessimistic: Math.round(n * 1.2 * (fte ? 10 : 1)) / (fte ? 10 : 1),
        source: 'extracted' as const,
      };
    });
}

function playbookBadge(level: PlaybookConfidence): { label: string; className: string } {
  if (level === 'confirmed') return { label: 'Playbook confirmed', className: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
  if (level === 'partial') return { label: 'Reconstructed list', className: 'bg-amber-50 text-amber-900 border-amber-200' };
  return { label: 'Unconfirmed', className: 'bg-gray-100 text-gray-700 border-gray-200' };
}

function fillStateBadge(level: FillState): { label: string; className: string } {
  if (level === 'confirmed') return { label: 'Confirmed', className: 'bg-sky-50 text-sky-800 border-sky-200' };
  if (level === 'inferred') return { label: 'Inferred', className: 'bg-violet-50 text-violet-800 border-violet-200' };
  if (level === 'user_confirmed') return { label: 'User-confirmed', className: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
  if (level === 'conflict') return { label: 'Conflict', className: 'bg-red-50 text-red-800 border-red-200' };
  if (level === 'not_found') return { label: 'Not found', className: 'bg-gray-100 text-gray-600 border-gray-200' };
  return { label: 'Best-guess', className: 'bg-amber-50 text-amber-900 border-amber-200' };
}

function formatValue(row: PacketField): string {
  const { field, value } = row;
  if (field.fieldType === 'currency' && typeof value === 'number') {
    return `$${value.toLocaleString('en-US')}`;
  }
  return valueToString(value);
}

function isCopyable(fieldType: PacketField['field']['fieldType']): boolean {
  return ['free_text_short', 'free_text_long', 'number', 'currency', 'reference_lookup'].includes(fieldType);
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const IntakeNeedList: React.FC<{ items: IntakeNeed[] }> = ({ items }) => (
  <ul className="mt-2 space-y-3">
    {items.map((item) => (
      <li key={item.id} className="text-sm">
        <p className="font-semibold text-super-dark-purple">{item.title}</p>
        <p className="text-gray-700 mt-0.5 leading-relaxed">{item.detail}</p>
        <p className="text-xs text-gray-500 mt-1">{item.unlocks}</p>
      </li>
    ))}
  </ul>
);

const Badge: React.FC<{ label: string; className: string }> = ({ label, className }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${className}`}>
    {label}
  </span>
);

const CopyButton: React.FC<{ text: string; label?: string }> = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        const ok = await copyText(text);
        if (ok) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        }
      }}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border border-gray-200 bg-white text-super-dark-purple hover:border-electric-blue hover:bg-sky-50"
    >
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <ClipboardCopy className="w-3.5 h-3.5" />}
      {copied ? 'Copied' : label}
    </button>
  );
};

const FieldCard: React.FC<{
  row: PacketField;
  onEdit: (fieldId: string, value: string) => void;
  onConfirmOption: (fieldId: string, value: string) => void;
  walkthrough?: boolean;
}> = ({ row, onEdit, onConfirmOption, walkthrough }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(valueToString(row.value));
  const pb = playbookBadge(row.field.playbookConfidence);
  const ex = fillStateBadge(row.state);
  const display = formatValue(row);

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-super-dark-purple">{row.field.label}</p>
          <p className="text-xs text-gray-500 mt-0.5">{row.field.location} · {row.field.fieldType.replace(/_/g, ' ')}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge {...pb} />
          <Badge {...ex} />
          {row.confirmInServiceNow && (
            <Badge label="Confirm in ServiceNow" className="bg-orange-50 text-orange-800 border-orange-200" />
          )}
          {row.field.required === true && (
            <Badge label="Required" className="bg-super-dark-purple text-white border-super-dark-purple" />
          )}
        </div>
      </div>

      {walkthrough && (
        <ol className="px-4 py-3 bg-lavender-bg/60 space-y-1.5 border-b border-lavender-border">
          {row.field.clickPath.map((step, i) => (
            <li key={i} className="flex gap-2 text-sm text-gray-800">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-super-dark-purple text-white text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
              <span className="leading-snug">{step}</span>
            </li>
          ))}
        </ol>
      )}

      <div className="px-4 py-3 space-y-3">
        {row.field.warn && (
          <div className="flex gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            {row.field.warn}
          </div>
        )}

        <div>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {isCopyable(row.field.fieldType) ? 'Paste this' : 'Select this'}
            </p>
            <div className="flex items-center gap-1.5">
              <CopyButton text={display} label={isCopyable(row.field.fieldType) ? 'Copy value' : 'Copy label'} />
              <button
                type="button"
                onClick={() => {
                  setDraft(valueToString(row.value));
                  setEditing((v) => !v);
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-dusky-purple hover:bg-gray-50"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
            </div>
          </div>
          {editing ? (
            <div className="space-y-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={Math.min(10, display.split('\n').length + 2)}
                className="w-full border border-electric-blue rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-electric-blue/30"
              />
              <button
                type="button"
                onClick={() => {
                  onEdit(row.field.id, draft);
                  setEditing(false);
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-electric-blue text-super-dark-purple"
              >
                Save to packet
              </button>
            </div>
          ) : (
            <pre className="whitespace-pre-wrap break-words bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-sm text-gray-800 font-sans">{display || '—'}</pre>
          )}
          <p className="text-xs text-gray-500 mt-2">{row.field.copyGuidance}</p>
        </div>

        {row.field.options && row.field.options.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Known options</p>
            <div className="flex flex-wrap gap-1.5">
              {row.field.options.map((opt) => (
                <span
                  key={opt.value}
                  className={`text-[11px] px-2 py-1 rounded-md border ${
                    valueToString(row.value).includes(opt.value)
                      ? 'border-electric-blue bg-sky-50 text-super-dark-purple font-semibold'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  {opt.value}
                  {opt.confidence !== 'confirmed' ? ' · check live' : ''}
                </span>
              ))}
            </div>
            {row.field.optionsNote && <p className="text-xs text-gray-500 mt-1.5">{row.field.optionsNote}</p>}
          </div>
        )}

        {(row.sourceSnippet || row.rationale) && (
          <div className="text-xs text-gray-600 bg-white border border-dashed border-gray-200 rounded-lg px-3 py-2">
            {row.rationale && <p><span className="font-semibold text-gray-700">Why: </span>{row.rationale}</p>}
            {row.sourceSnippet && (
              <p className="mt-1 italic text-gray-500">
                {row.sourceDoc ? `${row.sourceDoc.name}: ` : ''}“{row.sourceSnippet}”
              </p>
            )}
          </div>
        )}

        {row.state === 'conflict' && row.candidates && row.candidates.length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 space-y-2">
            <p className="text-xs font-semibold text-red-900">Sources disagree — pick one</p>
            {row.candidates.map((c, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onEdit(row.field.id, Array.isArray(c.value) ? c.value.join(', ') : String(c.value))}
                className="block w-full text-left text-xs rounded-md border border-red-200 bg-white px-2 py-1.5 hover:border-red-400"
              >
                <span className="font-semibold">{Array.isArray(c.value) ? c.value.join(', ') : String(c.value)}</span>
                <span className="text-gray-500"> — {c.sourceDocId || c.source || 'source'}</span>
              </button>
            ))}
          </div>
        )}

        {row.confirmInServiceNow && (
          <button
            type="button"
            onClick={() => onConfirmOption(row.field.id, valueToString(row.value))}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-dusky-purple hover:text-super-dark-purple"
          >
            <Flag className="w-3.5 h-3.5" />
            Mark live option (saves in this browser for the next packet)
          </button>
        )}
      </div>
    </div>
  );
};

export interface GFDIntakeWorkspaceProps {
  demo?: boolean;
  loadSessionId?: number | null;
  onSessionChange?: (id: number | null) => void;
}

const DEFAULT_MANIFEST: GfdManifest = { drivers: [], assumptions: [], dependencies: [], inflateInternalEstimates: false };

export const GFDIntakeWorkspace: React.FC<GFDIntakeWorkspaceProps> = ({ demo, loadSessionId, onSessionChange }) => {
  const [tab, setTab] = useState<TabId>('intake');
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [manifest, setManifest] = useState<GfdManifest>(DEFAULT_MANIFEST);
  const [simResults, setSimResults] = useState<any>(null);
  const [applyPercentile, setApplyPercentile] = useState<50 | 80>(50);
  const [interviewMessages, setInterviewMessages] = useState<{ role: string; content: string }[]>([]);
  const [interviewInput, setInterviewInput] = useState('');
  const [interviewBusy, setInterviewBusy] = useState(false);
  const [researchQuery, setResearchQuery] = useState('cloud SIEM consolidation cost and 3-year run-rate savings ranges for a large bank');
  const [researchBusy, setResearchBusy] = useState(false);
  const [researchPackets, setResearchPackets] = useState<ResearchPacket[]>([]);
  const [researchError, setResearchError] = useState<string | null>(null);
  const [accuracyReport, setAccuracyReport] = useState<{ findings: AccuracyFinding[]; unresolvedRequired: string[]; exportBlocked: boolean } | null>(null);
  const [accuracyBusy, setAccuracyBusy] = useState(false);
  const [readiness, setReadiness] = useState<ReadinessItem[]>(DEFAULT_READINESS);
  const [simBusy, setSimBusy] = useState(false);
  const [wordBusy, setWordBusy] = useState(false);
  const [edits, setEdits] = useState<Record<string, FieldValue>>({});
  const [confirmations, setConfirmations] = useState<ConfirmedOption[]>(() => loadConfirmations());
  const [openDoc, setOpenDoc] = useState<string | null>(null);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [downloadNote, setDownloadNote] = useState<string | null>(null);
  const [packetFilter, setPacketFilter] = useState<PacketFilter>('all');
  const [initiative, setInitiative] = useState<SeedInitiative | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState('');
  const [championName, setChampionName] = useState('');
  const [championTitle, setChampionTitle] = useState('');
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [excelBusy, setExcelBusy] = useState(false);

  const playbook = useMemo(
    () => applyConfirmationsToPlaybook(SMBC_GFD_PLAYBOOK, confirmations),
    [confirmations]
  );
  const packet = useMemo(
    () => (initiative ? buildPacket(playbook, initiative, edits) : []),
    [playbook, initiative, edits]
  );
  const stats = packetStats(packet);
  const gaps = useMemo(() => packetGaps(packet), [packet]);
  const championEmail = useMemo(
    () => (initiative ? buildChampionEmail(playbook, initiative, stats) : ''),
    [playbook, initiative, stats]
  );

  useEffect(() => {
    if (!initiative) {
      setEdits({});
      return;
    }
    setEdits(loadEdits(initiative.id));
  }, [initiative?.id]);

  useEffect(() => {
    if (!loadSessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const session = await loadGfdSession(loadSessionId);
        if (cancelled || !session) return;
        setSessionId(session.id);
        setChampionName(session.champion_name || '');
        setChampionTitle(session.champion_title || '');
        setNotes(session.notes || '');
        setManifest(session.manifest || DEFAULT_MANIFEST);
        setSimResults(session.sim_results || null);
        setInterviewMessages(session.messages || []);
        setResearchPackets(session.research_packets || []);
        setReadiness(session.readiness?.length ? session.readiness : DEFAULT_READINESS);
        setAccuracyReport(session.accuracy_findings ? { findings: session.accuracy_findings, unresolvedRequired: [], exportBlocked: false } : null);
        const next = initiativeFromGenerate({
          id: `gfd-${session.id}`,
          name: session.initiative_name || session.title,
          summary: session.summary,
          notes: session.notes,
          championName: session.champion_name,
          championTitle: session.champion_title,
          documents: session.documents,
          fills: session.fills,
        });
        setInitiative(next);
        setTab('summary');
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [loadSessionId]);

  const handleEdit = (fieldId: string, raw: string) => {
    if (!initiative) return;
    const field = packet.find((row) => row.field.id === fieldId)?.field;
    let next: FieldValue = raw;
    if (field?.fieldType === 'multi_select') {
      next = raw.split(',').map((p) => p.trim()).filter(Boolean);
    } else if (field?.fieldType === 'number' || field?.fieldType === 'currency') {
      const n = Number(String(raw).replace(/[$,]/g, ''));
      next = Number.isFinite(n) ? n : raw;
    }
    const merged = { ...edits, [fieldId]: next };
    setEdits(merged);
    saveEdits(initiative.id, merged);
    if (sessionId) {
      patchGfdFills(sessionId, [{ fieldId, value: next, state: 'user_confirmed' }]).catch(() => {});
    }
  };

  const handleConfirm = (fieldId: string, value: string) => {
    setConfirmations(saveConfirmation(fieldId, value));
  };

  const handleGenerate = async () => {
    setGenerateError(null);
    if (!files.length && !notes.trim()) {
      setGenerateError('Upload at least one document or add consultant notes.');
      return;
    }
    setGenerating(true);
    try {
      const next = await generateGfdPacket({
        files,
        notes,
        playbook,
        championName,
        championTitle,
        sessionId,
      });
      setInitiative(next);
      if (next.sessionId) {
        setSessionId(next.sessionId);
        onSessionChange?.(next.sessionId);
      }
      setOpenDoc(next.documents[0]?.id ?? null);
      setEdits({});
      saveEdits(next.id, {});
      setPacketFilter(next.fills.some((f) => f.state === 'best_guess' || f.extractionConfidence === 'low') ? 'low' : 'all');
      setManifest((prev) => (
        prev.drivers.length ? prev : { ...prev, drivers: seedDriversFromInitiative(next) }
      ));
      if (next.sessionId) {
        runGfdAccuracy(next.sessionId, playbook).then(setAccuracyReport).catch(() => {});
      }
      setTab('summary');
    } catch (err: any) {
      const status = err?.response?.status;
      const apiMsg = err?.response?.data?.error;
      const noResponse = !err?.response;
      const networkCut = noResponse && /network error|timeout of \d+ms exceeded/i.test(err?.message || '');
      if (status === 401) {
        setGenerateError('Jarvis login is required to run the LLM on your documents. Load the sample packet to review the walkthrough without a backend, or open GFD Intake from Jarvis Intelligence while signed in.');
      } else if (networkCut) {
        setGenerateError('The connection dropped while Jarvis was still reading your documents (this often takes 30–90 seconds). Retry once. If it happens again, the request is being cut off before a response arrives.');
      } else {
        setGenerateError(apiMsg || err?.message || 'Could not generate the packet.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const addFiles = (incoming: File[]) => {
    const accepted: File[] = [];
    const rejected: string[] = [];
    incoming.forEach((file) => {
      if (ALLOWED_FILE.test(file.name)) accepted.push(file);
      else rejected.push(file.name);
    });
    if (accepted.length) {
      setFiles((prev) => {
        const merged = [...prev, ...accepted];
        return merged.slice(0, 20);
      });
    }
    setRejectedFiles(rejected);
    if (accepted.length && generateError) setGenerateError(null);
  };

  const loadSample = () => {
    setInitiative(SEED_INITIATIVE);
    setChampionName(SEED_INITIATIVE.champion.name);
    setChampionTitle(SEED_INITIATIVE.champion.title);
    setNotes(SEED_INITIATIVE.consultantNotes);
    setOpenDoc(SEED_INITIATIVE.documents[0]?.id ?? null);
    setGenerateError(null);
    setReadiness(DEFAULT_READINESS);
    setManifest((prev) => (
      prev.drivers.length ? prev : { ...prev, drivers: seedDriversFromInitiative(SEED_INITIATIVE) }
    ));
    setPacketFilter('all');
    setTab('summary');
  };

  const goPacket = (filter: PacketFilter = 'all') => {
    setPacketFilter(filter);
    setTab('packet');
  };

  const noteDownload = (label: string) => {
    setDownloadNote(label);
    window.setTimeout(() => setDownloadNote(null), 2500);
  };

  const NeedPacket: React.FC = () => (
    <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
      <p className="text-sm font-semibold text-super-dark-purple">No packet yet</p>
      <p className="text-sm text-gray-600 mt-1">
        Upload project documents and notes on Documents, or load the sample to click through without an LLM.
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        <button
          type="button"
          onClick={() => setTab('intake')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-electric-blue text-super-dark-purple text-sm font-semibold"
        >
          Go to Documents
        </button>
        <button
          type="button"
          onClick={loadSample}
          className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-800"
        >
          Load sample packet
        </button>
      </div>
    </div>
  );

  const title = initiative?.name || 'GFD Intake';
  const championLine = initiative
    ? `Champion: ${initiative.champion.name}${initiative.champion.title ? ` · ${initiative.champion.title}` : ''}`
    : 'Upload project materials, then generate a champion packet.';

  return (
    <div className="space-y-4">
      {demo && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <span className="font-semibold">Interactive demo.</span>{' '}
          Start on Documents: generate from real files if this browser is signed into Jarvis, or load the seeded Cloud Detection example to click through without an LLM.
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-super-dark-purple text-electric-blue flex-shrink-0">
              <DoorOpen className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-dusky-purple">GFD Intake</p>
              <h2 className="text-xl font-bold text-super-dark-purple">{title}</h2>
              <p className="text-sm text-gray-600 mt-1">{championLine}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-700">
              {initiative ? `${stats.total} fields in packet` : 'No packet yet'}
            </span>
            {initiative && stats.low > 0 && (
              <span className="px-3 py-1.5 rounded-lg bg-gray-100 border border-gray-200 text-gray-700">
                {stats.low} low confidence
              </span>
            )}
            {initiative && (
              <span className="px-3 py-1.5 rounded-lg bg-orange-50 border border-orange-200 text-orange-900">
                {stats.confirmInSn} confirm in ServiceNow
              </span>
            )}
            {initiative && stats.hidden > 0 && (
              <span className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-600">
                {stats.hidden} hidden by answers
              </span>
            )}
            {confirmations.length > 0 && (
              <span className="px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900">
                {confirmations.length} live options remembered
              </span>
            )}
          </div>
        </div>
        {initiative?.consultantNotes && (
          <p className="text-sm text-gray-600 mt-3">{initiative.consultantNotes}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((item) => {
          const locked = !initiative && item.id !== 'intake';
          return (
            <button
              key={item.id}
              type="button"
              title={locked ? 'Generate or load a sample first' : undefined}
              onClick={() => setTab(item.id)}
              className={`px-3.5 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                tab === item.id
                  ? 'bg-super-dark-purple text-white border-super-dark-purple'
                  : locked
                    ? 'bg-white text-gray-400 border-gray-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-dusky-purple'
              }`}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {tab === 'intake' && (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.15fr)_minmax(22rem,0.85fr)] gap-4 items-start">
          <div className="space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h3 className="text-sm font-bold text-super-dark-purple">1. Files and notes in → champion packet out</h3>
              <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                Drop every relevant file, then type anything that is not in those files. Claude maps the corpus onto Stage 1. You stay the author; the champion never logs into Jarvis.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                PDF, Word, PowerPoint, Excel, CSV, text, Markdown, JSON, HTML, RTF, EML · max 20 files, 40 MB each. Live extraction needs a signed-in Intelligence session. The sample packet works with no API call.
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h3 className="text-sm font-bold text-super-dark-purple mb-3">Source documents</h3>
              <label
                className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center hover:border-dusky-purple"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  addFiles(Array.from(e.dataTransfer.files || []));
                }}
              >
                <Upload className="h-8 w-8 text-dusky-purple" />
                <span className="mt-2 text-sm font-semibold text-super-dark-purple">Add files</span>
                <span className="mt-1 text-xs text-gray-500">Click or drop · {files.length}/20 files</span>
                <input
                  type="file"
                  className="sr-only"
                  multiple
                  accept={ACCEPT_EXT}
                  onChange={(e) => {
                    addFiles(Array.from(e.target.files || []));
                    e.target.value = '';
                  }}
                />
              </label>
              {rejectedFiles.length > 0 && (
                <p className="mt-2 text-xs text-amber-800">
                  Skipped unsupported type: {rejectedFiles.join(', ')}
                </p>
              )}
              {files.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    >
                      <span className="truncate text-gray-800">
                        {f.name}{' '}
                        <span className="text-xs text-gray-500">({Math.max(1, Math.round(f.size / 1024))} KB)</span>
                      </span>
                      <button
                        type="button"
                        className="ml-2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        aria-label={`Remove ${f.name}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h3 className="text-sm font-bold text-super-dark-purple">Additional context / notes</h3>
              <p className="text-xs text-gray-500 mt-1">
                If a minimum item on the right is not in a file, type it here — names, FY numbers, “don’t know” on issues, what not to say.
              </p>
              <textarea
                className="mt-3 min-h-[180px] w-full rounded-xl border border-gray-300 px-3 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-electric-blue/30"
                placeholder="e.g. Champion is Marcus Chen, VP. This consolidates two SIEM stacks. Do not mention vendor names in the public description. Target go-live Q2. The requestor already briefed CISO informally…"
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  if (generateError) setGenerateError(null);
                }}
              />
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="text-sm">
                  <span className="font-medium text-gray-700">Champion name (on the packet)</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    placeholder="Marcus Chen"
                    value={championName}
                    onChange={(e) => setChampionName(e.target.value)}
                  />
                </label>
                <label className="text-sm">
                  <span className="font-medium text-gray-700">Champion title</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    placeholder="VP, Cyber Defense"
                    value={championTitle}
                    onChange={(e) => setChampionTitle(e.target.value)}
                  />
                </label>
              </div>
            </div>

            {generateError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {generateError}
              </div>
            )}
            {generating && (
              <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950">
                Claude is reading the files and filling Stage 1. This often takes 30–90 seconds; large decks can take a few minutes — leave this tab open.
              </div>
            )}
            {!championName.trim() && (files.length > 0 || notes.trim()) && !generating && (
              <p className="text-xs text-amber-800">
                Champion name is empty — the packet will say “Internal champion.” Fill it if you know who will type in Employee Center.
              </p>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={generating}
                onClick={handleGenerate}
                className="inline-flex items-center gap-2 rounded-xl bg-super-dark-purple px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Generate champion packet
              </button>
              <button
                type="button"
                disabled={generating}
                onClick={loadSample}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 disabled:opacity-60"
              >
                Load sample packet
              </button>
            </div>
          </div>

          <aside className="rounded-xl border border-gray-200 bg-white p-5 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto">
            <h3 className="text-sm font-bold text-super-dark-purple">What to bring</h3>
            <p className="text-sm text-gray-700 mt-2 leading-relaxed">{playbook.intakeGuidance.headline}</p>
            <p className="text-xs text-gray-500 mt-2">{playbook.intakeGuidance.caveat}</p>
            <div className="mt-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-super-dark-purple">Minimum — substance GFD actually scores</p>
              <IntakeNeedList items={playbook.intakeGuidance.minimum} />
            </div>
            <details className="mt-4 border-t border-gray-100 pt-4">
              <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wider text-gray-600">
                Best results — files that map cleanly
              </summary>
              <IntakeNeedList items={playbook.intakeGuidance.better} />
            </details>
            <details className="mt-3 border-t border-amber-100 pt-3">
              <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wider text-amber-900">
                Do not wait for these
              </summary>
              <IntakeNeedList items={playbook.intakeGuidance.doNotWait} />
            </details>
          </aside>
        </div>
      )}

      {tab === 'summary' && !initiative && <NeedPacket />}

      {tab === 'summary' && initiative && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h3 className="text-sm font-bold text-super-dark-purple mb-2">What this is</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Stemmler cannot sit in SMBC ServiceNow. This workspace turns the Global Front Door field map into a packet a named internal champion can execute: exact clicks, dropdown values, and paste blocks for every Stage 1 field. Stages 2–5 are expectation-setting only — those personas work Native View and are not who we brief to type.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => goPacket(gaps.queue.length ? 'queue' : 'all')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-electric-blue text-super-dark-purple text-sm font-semibold"
                >
                  {gaps.queue.length ? `Resolve ${gaps.queue.length} clarification items` : 'Review mapped fields'}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => goPacket('all')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700"
                >
                  Review mapped fields
                </button>
                <button
                  type="button"
                  onClick={() => setTab('export')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700"
                >
                  Skip to export
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {(gaps.empty.length > 0 || gaps.low.length > 0 || gaps.confirm.length > 0) && (
              <div className="rounded-xl border border-orange-200 bg-orange-50 p-5">
                <h3 className="text-sm font-bold text-super-dark-purple">Review these before you hand off</h3>
                <p className="text-sm text-gray-700 mt-1">
                  Yellow dropdowns are reconstructed. Low-confidence and empty fields are where the corpus was thin — edit them here or add notes and regenerate.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {gaps.confirm.length > 0 && (
                    <button type="button" onClick={() => goPacket('confirm')} className="px-3 py-1.5 rounded-lg bg-white border border-orange-200 text-xs font-semibold text-orange-950">
                      {gaps.confirm.length} confirm in ServiceNow
                    </button>
                  )}
                  {gaps.low.length > 0 && (
                    <button type="button" onClick={() => goPacket('low')} className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-semibold text-gray-800">
                      {gaps.low.length} low confidence
                    </button>
                  )}
                  {gaps.empty.length > 0 && (
                    <button type="button" onClick={() => goPacket('empty')} className="px-3 py-1.5 rounded-lg bg-white border border-red-200 text-xs font-semibold text-red-900">
                      {gaps.empty.length} empty
                    </button>
                  )}
                  {gaps.hidden > 0 && (
                    <span className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-xs text-gray-600">
                      {gaps.hidden} hidden by answers (e.g. which program)
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              {playbook.warnings.map((w) => (
                <div
                  key={w.id}
                  className={`rounded-xl border px-4 py-3 text-sm ${
                    w.severity === 'critical'
                      ? 'border-red-200 bg-red-50 text-red-950'
                      : w.severity === 'important'
                        ? 'border-amber-200 bg-amber-50 text-amber-950'
                        : 'border-gray-200 bg-gray-50 text-gray-800'
                  }`}
                >
                  <p className="font-semibold">{w.title}</p>
                  <p className="mt-1 leading-relaxed">{w.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Context documents</p>
              {initiative.documents.map((doc) => {
                const open = openDoc === doc.id;
                return (
                  <div key={doc.id} className="border-b border-gray-100 last:border-0 py-2">
                    <button
                      type="button"
                      onClick={() => setOpenDoc(open ? null : doc.id)}
                      className="w-full flex items-start gap-2 text-left"
                    >
                      <FileText className="w-4 h-4 text-dusky-purple mt-0.5 flex-shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-super-dark-purple">{doc.name}</span>
                        <span className="block text-xs text-gray-500">{doc.kind}</span>
                      </span>
                      {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    </button>
                    {open && (
                      <div className="mt-2 ml-6">
                        <p className="text-xs text-gray-600 mb-2">{doc.summary}</p>
                        <pre className="whitespace-pre-wrap text-xs text-gray-700 bg-gray-50 rounded-lg p-2.5 font-sans leading-relaxed">{doc.excerpt}</pre>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Attachments checklist</p>
              {playbook.attachments.map((a) => (
                <div key={a.id} className="flex gap-2 py-1.5 text-sm">
                  <Paperclip className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-800">{a.name}</p>
                    <p className="text-xs text-gray-500">{a.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'packet' && !initiative && <NeedPacket />}

      {tab === 'packet' && initiative && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {([
                ['all', `All (${stats.total})`],
                ['queue', `Needs decision (${gaps.queue.length})`],
                ['confirm', `Confirm in SN (${stats.confirmInSn})`],
                ['low', `Best-guess (${stats.bestGuess})`],
                ['empty', `Not found (${stats.notFound})`],
              ] as [PacketFilter, string][]).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPacketFilter(id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                    packetFilter === id
                      ? 'bg-super-dark-purple text-white border-super-dark-purple'
                      : 'bg-white text-gray-600 border-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setTab('queue')}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-electric-blue text-super-dark-purple text-xs font-semibold"
              >
                Clarification queue
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setTab('export')}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700"
              >
                Export
              </button>
            </div>
          </div>
          {playbook.sections.map((section) => {
            const rows = packet.filter((row) => {
              if (!row.visible || !section.fields.some((f) => f.id === row.field.id)) return false;
              if (packetFilter === 'confirm') return row.confirmInServiceNow;
              if (packetFilter === 'low') return row.state === 'best_guess';
              if (packetFilter === 'empty') return row.state === 'not_found' || isEmptyValue(row.value);
              if (packetFilter === 'queue') return ['best_guess', 'not_found', 'conflict'].includes(row.state);
              return true;
            });
            if (!rows.length) return null;
            return (
              <section key={section.id}>
                <h3 className="text-base font-bold text-super-dark-purple mb-1">{section.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{section.intro}</p>
                <div className="space-y-3">
                  {rows.map((row) => (
                    <FieldCard key={row.field.id} row={row} onEdit={handleEdit} onConfirmOption={handleConfirm} />
                  ))}
                </div>
              </section>
            );
          })}
          {packetFilter !== 'all' && !packet.some((row) => {
            if (!row.visible) return false;
            if (packetFilter === 'confirm') return row.confirmInServiceNow;
            if (packetFilter === 'low') return row.state === 'best_guess';
            if (packetFilter === 'empty') return row.state === 'not_found' || isEmptyValue(row.value);
            if (packetFilter === 'queue') return ['best_guess', 'not_found', 'conflict'].includes(row.state);
            return true;
          }) && (
            <p className="text-sm text-gray-600 text-center py-8">Nothing in this filter. Switch to All to see the full packet.</p>
          )}
        </div>
      )}

      {tab === 'queue' && !initiative && <NeedPacket />}
      {tab === 'queue' && initiative && (
        <div className="space-y-3">
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-bold text-super-dark-purple">Clarification queue</h3>
            <p className="text-sm text-gray-600 mt-1">
              Required fields tagged Best-guess, Not found, or Conflict. Answering sets User-confirmed and removes the item.
            </p>
            <p className="text-xs text-gray-500 mt-2">{gaps.queue.length} of {stats.total} required visible fields still need a decision.</p>
          </div>
          {playbook.sections.map((section) => {
            const rows = packet.filter((row) => row.visible && isRequiredVisible(row) && ['best_guess', 'not_found', 'conflict'].includes(row.state) && section.fields.some((f) => f.id === row.field.id));
            if (!rows.length) return null;
            return (
              <section key={section.id} className="space-y-3">
                <h3 className="text-base font-bold text-super-dark-purple">{section.title}</h3>
                {rows.map((row) => (
                  <FieldCard key={row.field.id} row={row} onEdit={handleEdit} onConfirmOption={handleConfirm} walkthrough />
                ))}
              </section>
            );
          })}
          {gaps.queue.length === 0 && (
            <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">Queue is clear. Continue to the business case studio.</p>
          )}
        </div>
      )}

      {tab === 'studio' && !initiative && <NeedPacket />}
      {tab === 'studio' && initiative && (
        <div className="space-y-4">
          {!sessionId && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              Generate a packet while signed in to persist this studio (interview, research, and Monte Carlo) on the server.
            </div>
          )}
          <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
            <h3 className="text-sm font-bold text-super-dark-purple">Interview — extract ranges</h3>
            <p className="text-sm text-gray-600">Asks only about Q3, Q4, Q20–Q26, Q30, Q31 drivers. Lists of assumptions/dependencies map to count tiers.</p>
            <div className="max-h-56 overflow-auto space-y-2 bg-gray-50 rounded-lg p-3 text-sm">
              {interviewMessages.length === 0 && <p className="text-gray-500">No interview yet. Send a message or click Start interview.</p>}
              {interviewMessages.map((m, i) => (
                <p key={i} className={m.role === 'user' ? 'text-super-dark-purple font-medium' : 'text-gray-700'}>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 mr-2">{m.role}</span>
                  {m.content}
                </p>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={interviewInput}
                onChange={(e) => setInterviewInput(e.target.value)}
                placeholder="e.g. FY cost most likely $3.2M, optimistic $2.8M, pessimistic $4.1M"
              />
              <button
                type="button"
                disabled={!sessionId || interviewBusy}
                onClick={async () => {
                  if (!sessionId) return;
                  const nextMsgs = interviewInput.trim()
                    ? [...interviewMessages, { role: 'user', content: interviewInput.trim() }]
                    : interviewMessages;
                  setInterviewBusy(true);
                  try {
                    const res = await runGfdInterview(sessionId, nextMsgs, manifest);
                    setInterviewMessages([...nextMsgs, res.message]);
                    setManifest(res.manifest);
                    setInterviewInput('');
                  } finally {
                    setInterviewBusy(false);
                  }
                }}
                className="px-4 py-2 rounded-xl bg-super-dark-purple text-white text-sm font-semibold disabled:opacity-50"
              >
                {interviewBusy ? 'Working…' : interviewMessages.length ? 'Send' : 'Start interview'}
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-bold text-super-dark-purple">Drivers (O / ML / P)</h3>
              <label className="text-xs flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!manifest.inflateInternalEstimates}
                  onChange={(e) => setManifest({ ...manifest, inflateInternalEstimates: e.target.checked })}
                />
                Inflate internal estimates 25%
              </label>
            </div>
            {(manifest.drivers || []).map((d, idx) => (
              <div key={d.id || idx} className="grid grid-cols-2 lg:grid-cols-7 gap-2 text-xs items-end">
                <label className="col-span-2">Name
                  <input className="mt-1 w-full border rounded px-2 py-1" value={d.name} onChange={(e) => {
                    const drivers = [...manifest.drivers];
                    drivers[idx] = { ...d, name: e.target.value };
                    setManifest({ ...manifest, drivers });
                  }} />
                </label>
                <label>Field
                  <input className="mt-1 w-full border rounded px-2 py-1" value={d.gfdFieldId} onChange={(e) => {
                    const drivers = [...manifest.drivers];
                    drivers[idx] = { ...d, gfdFieldId: e.target.value };
                    setManifest({ ...manifest, drivers });
                  }} />
                </label>
                <label>O
                  <input type="number" className="mt-1 w-full border rounded px-2 py-1" value={d.optimistic} onChange={(e) => {
                    const drivers = [...manifest.drivers];
                    drivers[idx] = { ...d, optimistic: Number(e.target.value) };
                    setManifest({ ...manifest, drivers });
                  }} />
                </label>
                <label>ML
                  <input type="number" className="mt-1 w-full border rounded px-2 py-1" value={d.most_likely} onChange={(e) => {
                    const drivers = [...manifest.drivers];
                    drivers[idx] = { ...d, most_likely: Number(e.target.value) };
                    setManifest({ ...manifest, drivers });
                  }} />
                </label>
                <label>P
                  <input type="number" className="mt-1 w-full border rounded px-2 py-1" value={d.pessimistic} onChange={(e) => {
                    const drivers = [...manifest.drivers];
                    drivers[idx] = { ...d, pessimistic: Number(e.target.value) };
                    setManifest({ ...manifest, drivers });
                  }} />
                </label>
                <button type="button" className="text-red-700 text-xs" onClick={() => setManifest({ ...manifest, drivers: manifest.drivers.filter((_, i) => i !== idx) })}>Remove</button>
              </div>
            ))}
            <button
              type="button"
              className="text-xs font-semibold text-dusky-purple"
              onClick={() => setManifest({
                ...manifest,
                drivers: [...manifest.drivers, { id: `d-${Date.now()}`, gfdFieldId: 'fy_cost', name: 'New driver', optimistic: 0, most_likely: 0, pessimistic: 0, source: 'user_stated' }],
              })}
            >
              Add driver
            </button>
            {initiative && (
              <button
                type="button"
                className="ml-3 text-xs font-semibold text-dusky-purple"
                onClick={() => {
                  const seeded = seedDriversFromInitiative(initiative);
                  const have = new Set(manifest.drivers.map((d) => d.gfdFieldId));
                  setManifest({ ...manifest, drivers: [...manifest.drivers, ...seeded.filter((d) => !have.has(d.gfdFieldId))] });
                }}
              >
                Seed from packet numbers
              </button>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-xs">Assumptions (one per line)
                <textarea className="mt-1 w-full border rounded px-2 py-1 min-h-[80px]" value={(manifest.assumptions || []).map((a) => a.text).join('\n')} onChange={(e) => setManifest({ ...manifest, assumptions: e.target.value.split('\n').filter(Boolean).map((text, i) => ({ id: `a-${i}`, text, source: 'user_stated' })) })} />
              </label>
              <label className="text-xs">Dependencies (one per line)
                <textarea className="mt-1 w-full border rounded px-2 py-1 min-h-[80px]" value={(manifest.dependencies || []).map((a) => a.text).join('\n')} onChange={(e) => setManifest({ ...manifest, dependencies: e.target.value.split('\n').filter(Boolean).map((text, i) => ({ id: `dep-${i}`, text, source: 'user_stated' })) })} />
              </label>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
            <h3 className="text-sm font-bold text-super-dark-purple">Web research (cited)</h3>
            <p className="text-xs text-gray-500">Benchmarks become web_benchmark drivers — never Confirmed. SMBC-internal IDs are out of scope.</p>
            <div className="flex gap-2">
              <input className="flex-1 rounded-lg border px-3 py-2 text-sm" value={researchQuery} onChange={(e) => setResearchQuery(e.target.value)} />
              <button
                type="button"
                disabled={!sessionId || researchBusy}
                onClick={async () => {
                  if (!sessionId) return;
                  setResearchBusy(true);
                  setResearchError(null);
                  try {
                    const res = await runGfdResearch(sessionId, researchQuery, 'fy_cost');
                    setResearchPackets((prev) => [...prev, res.packet]);
                    setManifest(res.manifest);
                    if (res.fills) {
                      setInitiative((prev) => prev ? { ...prev, fills: res.fills } : prev);
                    }
                  } catch (err: any) {
                    setResearchError(err?.response?.data?.error || err?.message || 'Research failed');
                  } finally {
                    setResearchBusy(false);
                  }
                }}
                className="px-4 py-2 rounded-xl bg-electric-blue text-super-dark-purple text-sm font-semibold disabled:opacity-50"
              >
                {researchBusy ? 'Searching…' : 'Pre-populate ranges'}
              </button>
            </div>
            {researchError && <p className="text-xs text-amber-800">{researchError}</p>}
            {researchPackets.map((p, i) => (
              <div key={i} className="text-xs border border-dashed rounded-lg p-3">
                <p className="font-semibold">{p.query}</p>
                <p className="text-gray-600 mt-1 line-clamp-4">{p.content}</p>
                <ul className="mt-2 space-y-1">
                  {(p.sources || []).map((s) => (
                    <li key={s.url}><a className="text-electric-blue underline" href={s.url} target="_blank" rel="noreferrer">[{s.index}] {s.title}</a>{s.excerpt ? ` — ${s.excerpt.slice(0, 180)}` : ''}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
            <h3 className="text-sm font-bold text-super-dark-purple">Monte Carlo</h3>
            <div className="flex flex-wrap gap-2 items-center">
              <button
                type="button"
                disabled={!sessionId || simBusy || manifest.drivers.length === 0}
                onClick={async () => {
                  if (!sessionId) return;
                  setSimBusy(true);
                  try {
                    const res = await runGfdSimulation(sessionId, manifest);
                    setSimResults(res.simResults);
                    setManifest(res.manifest);
                  } finally {
                    setSimBusy(false);
                  }
                }}
                className="px-4 py-2 rounded-xl bg-super-dark-purple text-white text-sm font-semibold disabled:opacity-50"
              >
                {simBusy ? 'Running 10k…' : 'Run simulation'}
              </button>
              <label className="text-xs">Apply
                <select className="ml-2 border rounded px-2 py-1" value={applyPercentile} onChange={(e) => setApplyPercentile(Number(e.target.value) === 80 ? 80 : 50)}>
                  <option value={50}>P50 (recommended)</option>
                  <option value={80}>P80</option>
                </select>
              </label>
              <button
                type="button"
                disabled={!sessionId || !simResults}
                onClick={async () => {
                  if (!sessionId) return;
                  const res = await applyGfdSimulation(sessionId, applyPercentile);
                  setInitiative((prev) => prev ? { ...prev, fills: res.fills } : prev);
                  setEdits({});
                  setTab('packet');
                }}
                className="px-4 py-2 rounded-xl bg-electric-blue text-super-dark-purple text-sm font-semibold disabled:opacity-50"
              >
                Apply {`P${applyPercentile}`} to packet
              </button>
            </div>
            {simResults?.rollups && (
              <div className="space-y-4">
                <div className="overflow-auto">
                  <table className="text-xs w-full">
                    <thead><tr className="text-left text-gray-500"><th className="py-1">Field</th><th>P10</th><th>P50</th><th>P80</th><th>P90</th><th>Band</th></tr></thead>
                    <tbody>
                      {Object.entries(simResults.rollups).map(([id, r]: [string, any]) => (
                        <tr key={id} className="border-t">
                          <td className="py-1 font-medium">{id}</td>
                          <td>{r.p10 != null ? Math.round(r.p10).toLocaleString() : ''}</td>
                          <td>{r.p50 != null ? Math.round(r.p50).toLocaleString() : r.count}</td>
                          <td>{r.p80 != null ? Math.round(r.p80).toLocaleString() : ''}</td>
                          <td>{r.p90 != null ? Math.round(r.p90).toLocaleString() : ''}</td>
                          <td>{r.mappedBand || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {simResults.rollups.fy_cost?.histogram?.bins && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">FY cost histogram</p>
                    <div className="flex items-end gap-0.5 h-24">
                      {simResults.rollups.fy_cost.histogram.bins.map((bin: { count: number }, i: number) => {
                        const max = Math.max(...simResults.rollups.fy_cost.histogram.bins.map((b: { count: number }) => b.count), 1);
                        return <div key={i} className="flex-1 bg-electric-blue/80 rounded-t" style={{ height: `${Math.max(4, (bin.count / max) * 100)}%` }} title={String(bin.count)} />;
                      })}
                    </div>
                  </div>
                )}
                {Array.isArray(simResults.sensitivity) && simResults.sensitivity.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Sensitivity (Spearman)</p>
                    <div className="space-y-1">
                      {simResults.sensitivity.slice(0, 8).map((row: { driverId: string; name: string; spearman: number }) => (
                        <div key={row.driverId} className="flex items-center gap-2 text-xs">
                          <span className="w-40 truncate">{row.name}</span>
                          <div className="flex-1 h-2 bg-gray-100 rounded overflow-hidden">
                            <div className="h-2 bg-super-dark-purple" style={{ width: `${Math.min(100, Math.abs(row.spearman || 0) * 100)}%` }} />
                          </div>
                          <span className="w-12 text-right">{(row.spearman || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'accuracy' && !initiative && <NeedPacket />}
      {tab === 'accuracy' && initiative && (
        <div className="space-y-3">
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-bold text-super-dark-purple">Accuracy cross-check</h3>
            <p className="text-sm text-gray-600 mt-1">Snippet grounding, dropdown membership, invented entities, ungrounded web claims. Approving a finding never overwrites a User-confirmed value without an explicit edit.</p>
            <button
              type="button"
              disabled={!sessionId || accuracyBusy}
              onClick={async () => {
                if (!sessionId) return;
                setAccuracyBusy(true);
                try {
                  const report = await runGfdAccuracy(sessionId, playbook);
                  setAccuracyReport(report);
                } finally {
                  setAccuracyBusy(false);
                }
              }}
              className="mt-3 px-4 py-2 rounded-xl bg-super-dark-purple text-white text-sm font-semibold disabled:opacity-50"
            >
              {accuracyBusy ? 'Running…' : 'Run verifier'}
            </button>
          </div>
          {(accuracyReport?.findings || []).map((f) => (
            <div key={f.id} className="rounded-xl border border-dashed border-gray-300 bg-white p-4">
              <p className="text-xs font-semibold uppercase text-gray-500">{f.check_id} · {f.severity}</p>
              <p className="text-sm text-gray-800 mt-1">{f.message}</p>
              <p className="text-xs text-gray-500 mt-1">Field: {f.fieldId || '—'} · {f.status}</p>
              {f.status === 'proposed' && sessionId && (
                <div className="mt-2 flex gap-2">
                  <button type="button" className="text-xs font-semibold px-3 py-1 rounded-lg bg-emerald-50 border border-emerald-200" onClick={async () => {
                    const res = await decideGfdFinding(sessionId, f.id, 'approve');
                    setInitiative((prev) => prev ? { ...prev, fills: res.fills } : prev);
                    setAccuracyReport((prev) => prev ? { ...prev, findings: res.findings } : prev);
                  }}>Approve</button>
                  <button type="button" className="text-xs font-semibold px-3 py-1 rounded-lg bg-red-50 border border-red-200" onClick={async () => {
                    const res = await decideGfdFinding(sessionId, f.id, 'reject');
                    setAccuracyReport((prev) => prev ? { ...prev, findings: res.findings } : prev);
                  }}>Reject</button>
                </div>
              )}
            </div>
          ))}
          {accuracyReport && accuracyReport.findings.length === 0 && (
            <p className="text-sm text-emerald-800">No findings. Packet is grounded enough to export, subject to required-field gating.</p>
          )}
        </div>
      )}

      {tab === 'readiness' && !initiative && <NeedPacket />}
      {tab === 'readiness' && initiative && (
        <div className="space-y-3">
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-bold text-super-dark-purple">Stage 2–4 readiness</h3>
            <p className="text-sm text-gray-600 mt-1">Checklist for Feasibility Fit, EA/Procurement screening, and Final Approval. Not form fields.</p>
          </div>
          {(readiness.length ? readiness : DEFAULT_READINESS).map((item) => (
            <div key={item.id} className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase text-dusky-purple">{item.stage}</p>
              <p className="text-sm font-semibold text-super-dark-purple mt-1">{item.item}</p>
              <p className="text-xs text-gray-500 mt-1">{item.why}</p>
              <div className="mt-2 flex flex-wrap gap-2 items-center">
                <select
                  className="text-xs border rounded px-2 py-1"
                  value={item.status}
                  onChange={(e) => {
                    const status = e.target.value as ReadinessItem['status'];
                    const base = readiness.length ? readiness : DEFAULT_READINESS;
                    const updated = base.map((r) => (r.id === item.id ? { ...r, status } : r));
                    setReadiness(updated);
                    if (sessionId) patchReadiness(sessionId, updated).catch(() => {});
                  }}
                >
                  <option value="ready">Ready</option>
                  <option value="not_ready">Not ready</option>
                  <option value="not_applicable">Not applicable</option>
                </select>
                <input
                  className="flex-1 min-w-[12rem] text-xs border rounded px-2 py-1"
                  placeholder="Note"
                  value={item.note}
                  onChange={(e) => {
                    const note = e.target.value;
                    setReadiness((prev) => {
                      const base = prev.length ? prev : DEFAULT_READINESS;
                      const updated = base.map((r) => (r.id === item.id ? { ...r, note } : r));
                      if (sessionId) patchReadiness(sessionId, updated).catch(() => {});
                      return updated;
                    });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'export' && !initiative && <NeedPacket />}

      {tab === 'export' && initiative && (
        <div className="space-y-3">
          {exportBlocked(packet) && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              Export is flagged: {stats.unresolvedRequired} required field{stats.unresolvedRequired === 1 ? '' : 's'} still Best-guess, Not found, or Conflict. Resolve them on Clarification before handing this to a sponsor.
            </div>
          )}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-bold text-super-dark-purple">Family 1 — ServiceNow paste pack</h3>
            <p className="text-sm text-gray-600 mt-1">
              Branded Word walkthrough (replaces PDF as the primary paste artifact) plus the Excel champion checklist with provenance and source columns.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!sessionId || wordBusy || exportBlocked(packet)}
                onClick={async () => {
                  if (!sessionId) return;
                  setWordBusy(true);
                  try {
                    await downloadWalkthroughDocx(sessionId, playbook, initiative.name);
                    noteDownload('Word walkthrough downloaded');
                  } finally {
                    setWordBusy(false);
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-super-dark-purple text-white text-sm font-semibold disabled:opacity-50"
              >
                {wordBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Download Word walkthrough
              </button>
              <button
                type="button"
                disabled={excelBusy}
                onClick={async () => {
                  setExcelBusy(true);
                  try {
                    await downloadChampionExcel(playbook, initiative, packet);
                    noteDownload('Excel checklist downloaded');
                  } finally {
                    setExcelBusy(false);
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-electric-blue text-super-dark-purple text-sm font-semibold disabled:opacity-60"
              >
                {excelBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                Download Excel checklist
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-bold text-super-dark-purple">Family 2 — Business case pack</h3>
            <p className="text-sm text-gray-600 mt-1">Assumptions, drivers, Monte Carlo P10/P50/P80/P90, mapped GFD bands, and citations.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!sessionId || exportBlocked(packet)}
                onClick={async () => {
                  if (!sessionId) return;
                  await downloadBusinessCaseDocx(sessionId, initiative.name);
                  noteDownload('Business-case Word downloaded');
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold disabled:opacity-50"
              >
                <FileText className="w-4 h-4" />
                Business-case Word
              </button>
              <button
                type="button"
                disabled={!sessionId}
                onClick={async () => {
                  if (!sessionId) return;
                  await downloadBusinessCaseXlsx(sessionId, initiative.name);
                  noteDownload('Business-case Excel downloaded');
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold disabled:opacity-50"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Business-case Excel
              </button>
            </div>
            {!sessionId && <p className="text-xs text-amber-800 mt-2">Word business-case export needs a saved server session (generate while signed in).</p>}
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-bold text-super-dark-purple">Email to paste</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={async () => {
                  const ok = await copyText(championEmail);
                  if (ok) {
                    setCopiedEmail(true);
                    setTimeout(() => setCopiedEmail(false), 1600);
                  }
                }}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold"
              >
                {copiedEmail ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <ClipboardCopy className="w-4 h-4" />}
                {copiedEmail ? 'Email copied' : 'Copy champion email'}
              </button>
            </div>
            <pre className="mt-3 whitespace-pre-wrap text-sm leading-relaxed bg-gray-50 rounded-xl p-4 max-h-[240px] overflow-auto">{championEmail}</pre>
          </div>
          {downloadNote && (
            <p className="text-xs font-semibold text-emerald-800">{downloadNote}</p>
          )}
        </div>
      )}

      <p className="text-[11px] text-gray-400 flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5" />
        Playbook {playbook.id}
        {initiative ? ` · packet ${initiative.id}` : ' · generate from documents or load the sample'}
        {' · edits stay in this browser until you clear site data'}
      </p>
    </div>
  );
};

export default GFDIntakeWorkspace;
